import * as React from 'react';
import { AdminSettingsSection, Button, ConfirmDialog, Input } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import {
  useDeleteMigrationSetupMutation,
  useSetMigrationOriginMutation,
  useSetMigrationProviderMutation,
  useTestMigrationOriginMutation,
} from 'src/api/mutations/migrationMutations';
import { MigrationOverview, MigrationOriginTestResult, MigrationProvider } from 'src/types/TMigration';
import { Note, Pill, Row, errorText } from './parts';
import { providerName } from './providers';
import styles from './styles.module.scss';

/**
 * "Your old provider" — who you are leaving, and how we reach them.
 *
 * Four fields, two of them required. Notably absent is the one the first
 * version of this screen asked for separately: the vendor account id. It is the
 * first path segment of the delivery URL above it, so migration-onramp reads it out and
 * registers it — asking for the same value twice only ever added a chance to
 * mistype the field that decides whose account a request is served from. See
 * `deriveIdentifier` in migration-onramp's adminapi.
 *
 * Cloudinary and ImageKit only. migration-onramp also supports a plain web server origin
 * and an operator can configure one from the command line, but it is not part
 * of this product yet: there are no vendor parameters to translate and no
 * library to list, so offering it here made the very first decision on the page
 * a confusing one for no benefit.
 *
 * The credentials are optional and the help says so before it says anything
 * else. Without them we still serve every public URL and copy every public
 * image; what they unlock is listing the library and reaching private images.
 * Demanding a third-party API secret up front, before explaining why, is how an
 * onboarding step turns into a support ticket.
 */

type Props = {
  overview: MigrationOverview;
};

export const ProviderSection: React.FC<Props> = ({ overview }) => {
  const { showSuccess, showAlert } = useAlerts();
  const saveProvider = useSetMigrationProviderMutation();
  const saveOrigin = useSetMigrationOriginMutation();
  const test = useTestMigrationOriginMutation();
  const deleteSetup = useDeleteMigrationSetupMutation();

  const [provider, setProvider] = React.useState<MigrationProvider | ''>(overview.provider || '');
  const [baseUrl, setBaseUrl] = React.useState(overview.origin?.base_url || '');
  const [apiKey, setApiKey] = React.useState('');
  const [apiSecret, setApiSecret] = React.useState('');
  const [result, setResult] = React.useState<MigrationOriginTestResult | null>(null);
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    setProvider(overview.provider || '');
    setBaseUrl(overview.origin?.base_url || '');
  }, [overview.provider, overview.origin?.base_url]);

  const stored = overview.origin;
  const isImageKit = provider === 'imagekit';
  const locked = Boolean(overview.provider);
  const hasStoredKey = Boolean(stored?.api_key_hint);

  const clearSetup = async () => {
    setConfirming(false);
    try {
      await deleteSetup.mutateAsync();
      setProvider('');
      setBaseUrl('');
      setApiKey('');
      setApiSecret('');
      setResult(null);
      showSuccess('Set-up cleared. Your copied images are untouched.');
    } catch (err) {
      showAlert(errorText(err, 'Could not clear the set-up'));
    }
  };

  const placeholder = isImageKit
    ? 'https://ik.imagekit.io/your-endpoint-id'
    : 'https://res.cloudinary.com/your-cloud-name';

  const dirty =
    provider !== (overview.provider || '') ||
    baseUrl.trim() !== (overview.origin?.base_url || '') ||
    apiKey.trim() !== '' ||
    apiSecret.trim() !== '';

  /**
   * Testable as soon as there is an address and something to authenticate with,
   * saved or typed.
   *
   * This used to require `stored.base_url`, so on a brand-new account — the one
   * screen where checking a credential matters most — the button was dead until
   * the customer had already committed everything.
   */
  const canTest =
    !!provider &&
    (!!baseUrl.trim() || !!stored?.base_url) &&
    (hasStoredKey || !!apiKey.trim() || !!apiSecret.trim());

  const save = async () => {
    if (!provider) {
      showAlert('Choose where your images are today');
      return;
    }
    if (!baseUrl.trim()) {
      showAlert('Enter the address your images come from');
      return;
    }
    try {
      // Provider and URL first: the credentials write needs the account to
      // exist, and migration-onramp registers the identifier as part of this call.
      const saved = await saveProvider.mutateAsync({
        provider,
        base_url: baseUrl.trim(),
        pull_enabled: overview.pull_enabled,
      });

      const key = apiKey.trim();
      const secret = apiSecret.trim();
      if (key || secret) {
        // Only fields actually typed are sent. An untouched password field must
        // never be read as "clear the stored secret", and the form only ever
        // holds a masked hint to begin with.
        await saveOrigin.mutateAsync({
          ...(key ? { api_key: key } : {}),
          ...(secret ? { api_secret: secret } : {}),
        });
        setApiKey('');
        setApiSecret('');
      }

      setResult(null);
      if (saved?.warning) {
        showAlert(saved.warning);
      } else {
        showSuccess('Saved');
        // Saving credentials without checking them is how a wrong key sits
        // unnoticed until a copy fails hours later. If any were supplied, the
        // save proves them straight away.
        if (key || secret) await runTest();
      }
    } catch (err) {
      showAlert(errorText(err, 'Could not save'));
    }
  };

  /**
   * Test whatever is in the form, falling back to what is stored.
   *
   * Typed credentials are sent to be tested and are NOT stored by the test, so
   * "does this key work?" can be answered before committing to it. Testing only
   * saved credentials meant a customer had to store a wrong key to discover it
   * was wrong, and was then left with a broken one saved.
   */
  const runTest = async () => {
    // Whatever is in the form wins over what is stored, including the address —
    // which is what lets a brand-new account check its very first entry. The
    // server tests a copy and stores none of it.
    const urlChanged = baseUrl.trim() !== (overview.provider ? overview.origin?.base_url || '' : '');
    const providerChanged = provider !== (overview.provider || '');
    const typed = {
      ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      ...(apiSecret.trim() ? { api_secret: apiSecret.trim() } : {}),
      ...(urlChanged && baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
      // The provider decides which vendor API the check calls, and on a
      // brand-new account there is nothing saved to read it from. Sending the
      // one showing in the dropdown is what makes "test before you save" work
      // at all — without it the server answered "choose a provider before
      // testing a connection" about the provider right there on screen.
      ...(providerChanged && provider ? { provider } : {}),
    };
    try {
      const res = await test.mutateAsync(Object.keys(typed).length ? typed : undefined);
      setResult(res);
      if (res.verified) showSuccess('Connected, and your account is verified');
    } catch (err) {
      showAlert(errorText(err, 'Could not reach your provider'));
    }
  };

  const clearCredentials = async () => {
    try {
      await saveOrigin.mutateAsync({ api_key: '', api_secret: '' });
      setResult(null);
      showSuccess('Credentials removed');
    } catch (err) {
      showAlert(errorText(err, 'Could not remove the credentials'));
    }
  };

  const busy = saveProvider.isLoading || saveOrigin.isLoading;

  return (
    <AdminSettingsSection title="Your old provider">
      <p className={styles.sectionLead}>Where your images are served from today, so we can fetch them.</p>

      {/*
        * Chosen once, on the provider cards the page shows before this section
        * exists, then shown here rather than offered again.
        *
        * An account has one provider and one origin (`uq_group` on both of
        * migration-onramp's tables), so there is no such thing as switching in
        * place — the previous vendor's URL, credentials and identifiers all have
        * to go first. A live dropdown here implied a switch the backend cannot
        * do, and half-changing it left a set-up that looked complete and served
        * nothing. Clearing is the honest route back to the cards, and it is
        * deliberately a separate, confirmed action.
        */}
      <Row
        label="Provider"
        help="An account migrates from one provider at a time. To move from a different one, clear this set-up first."
      >
        <Pill>{providerName(overview.provider as MigrationProvider)}</Pill>
      </Row>

      <Row
        label="Your images are at"
        stacked
        help="Copy this from any of your image URLs — everything up to and including your account id."
      >
        <Input value={baseUrl} onChange={setBaseUrl} placeholder={placeholder} />
      </Row>

      <Row
        label={isImageKit ? 'Private key' : 'API key'}
        help={
          hasStoredKey
            ? `Saved (${stored?.api_key_hint}). Type a new one to replace it.`
            : 'Optional. Lets us copy your whole library at once and reach your private images.'
        }
      >
        <Input value={apiKey} onChange={setApiKey} placeholder={hasStoredKey ? '••••••••' : 'Paste it here'} />
      </Row>

      {/* ImageKit authenticates with a private key alone. Asking for a secret it
          does not issue would send the customer hunting for a value that does
          not exist. */}
      {!isImageKit && (
        <Row
          label="API secret"
          help={stored?.has_secret ? 'Saved. Type a new one to replace it.' : 'Encrypted, and never shown again.'}
        >
          {/* A plain password input rather than the PasswordInput component,
              which renders our own password-strength rules — nonsense against a
              credential Cloudinary issued. */}
          <Input
            type="password"
            value={apiSecret}
            onChange={setApiSecret}
            placeholder={stored?.has_secret ? '••••••••' : 'Paste it here'}
          />
        </Row>
      )}

      <div className={styles.sectionActions}>
        <Button onClick={save} loading={busy} disabled={!dirty}>
          Save
        </Button>
        {/* Usable as soon as there is something to test — typed credentials or
            saved ones. It used to require saving first, which is backwards:
            checking a key is exactly what you want to do BEFORE keeping it. */}
        <Button
          mode="secondary"
          outlined
          onClick={runTest}
          loading={test.isLoading}
          disabled={!canTest}
        >
          Test connection
        </Button>
        {hasStoredKey && (
          <Button mode="flat" onClick={clearCredentials} disabled={busy}>
            Remove credentials
          </Button>
        )}
        {locked && (
          <Button
            mode="flat"
            onClick={() => setConfirming(true)}
            disabled={busy || deleteSetup.isLoading}
          >
            Clear this set-up
          </Button>
        )}
        {stored?.can_list && !result && <Pill tone="good">Connected</Pill>}
      </div>

      {confirming && (
        <ConfirmDialog
          title="Clear this migration set-up?"
          message={
            'This removes the provider, the address, your API credentials and the identifiers we ' +
            'match your URLs on — so you can choose a different provider. ' +
            'Images already copied across are NOT deleted and stay in your library. ' +
            'Your old image URLs will stop working straight away, so only do this if they are not live yet.'
          }
          onConfirm={clearSetup}
          onCancel={() => setConfirming(false)}
        />
      )}

      {result && (
        <Note tone={result.ok ? 'good' : 'warn'}>
          <div>{result.detail}</div>
          {/* Their own filenames coming back beats the words "credentials
              valid": it shows we reached the right account, not merely a
              reachable one. */}
          {result.sample && result.sample.length > 0 && (
            <ul className={styles.sampleList}>
              {result.sample.map((path) => (
                <li key={path}>
                  <code>{path}</code>
                </li>
              ))}
            </ul>
          )}
        </Note>
      )}
    </AdminSettingsSection>
  );
};

export default ProviderSection;
